import React, { useContext } from "react"
import { PlusIcon } from "@heroicons/react/20/solid"
import { NewReservationContext } from "~/contexts/admin/NewReservationContext"
import { RESERVATION_KINDS } from "~/constants"
import ResponsiveLineItemRow from "./ResponsiveLineItemRow"
import PortalHoverTip from "~/components/ui/PortalHoverTip"
import ILineItem from "~/interfaces/ILineItem"
import { SiteProps } from "~/interfaces"
import useBreakpoint from "~/hooks/useBreakpoint"
import { LINE_ITEM_KINDS } from "~/constants/line_item_kinds"

interface PricingSectionProps {
    sites: SiteProps[]
    camp: any
    selectProduct: (product: any, row: Partial<ILineItem>) => void
    createNewItem: (name: string, id?: string) => void
    deleteAddon: (row: ILineItem, isAddOn: boolean, isNew: boolean) => void
    dispatch: any
    isLoadingOrderPreview: boolean
    isPayingSeparately: boolean
    setLineItems: (items: ILineItem[]) => void
}

const ResponsivePricingSection: React.FC<PricingSectionProps> = ({
    sites,
    camp,
    selectProduct,
    createNewItem,
    deleteAddon,
    dispatch,
    isLoadingOrderPreview,
    isPayingSeparately,
    setLineItems,
}) => {
    const { lineItems, newReservation } = useContext(NewReservationContext)
    const isMobile = useBreakpoint("(max-width: 768px)")

    const isGroup = newReservation.kind === RESERVATION_KINDS.GROUP

    // Handle the Group paid separately case
    if (isGroup && isPayingSeparately) {
        return (
            <div className="flex items-center justify-center z-20 bg-white w-full">
                <div className="bg-white p-8 rounded-lg shadow-lg z-30 max-w-lg opacity-100">
                    <h2 className="text-2xl font-bold mb-4">Group paid separately</h2>
                    <p>
                        A link will be sent to the group leader, and can be forwarded to guests to pay themselves. There they'll
                        add their own add-ons and pay individually.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="overflow-hidden md:overflow-y-auto md:max-h-[500px] scrollbar-thin scroll-smooth pb-2">
            <h4 className="text-xl font-semibold mb-4">Pricing</h4>

            {isMobile && <>
                {lineItems?.map((row, index) => (
                    <ResponsiveLineItemRow
                        key={index}
                        row={row}
                        sites={sites}
                        selectProduct={selectProduct}
                        createNewItem={createNewItem}
                        deleteAddon={deleteAddon}
                        dispatch={dispatch}
                        camp={camp}
                        isLoadingOrderPreview={isLoadingOrderPreview}
                    />
                ))}
            </> || <div
                className="hidden md:grid gap-x-2 bg-white overflow-x-auto lg:overflow-x-visible scrollbar-thin scroll-smooth new-reservation-table"
                style={{ gridTemplateColumns: "auto 17.5% 10% 10% 20% 6%" }}
            >
                    <div className="text-md font-medium sticky -top-1 bg-white z-10 pb-2">Label</div>
                    <div className="text-md font-medium sticky -top-1 bg-white z-10 pb-2">Unit Price</div>
                    <div className="text-md font-medium sticky -top-1 bg-white z-10 pb-2">Nights</div>
                    <div className="text-md font-medium sticky -top-1 bg-white z-10 pb-2">Quantity</div>
                    <div className="text-md font-medium sticky -top-1 bg-white z-10 pb-2">Total Price</div>
                    <div className="sticky -top-1 bg-white z-10 pb-2" />

                    {!isMobile && lineItems?.map((row, index) => (
                        <ResponsiveLineItemRow
                            key={index}
                            row={row}
                            sites={sites}
                            selectProduct={selectProduct}
                            createNewItem={createNewItem}
                            deleteAddon={deleteAddon}
                            dispatch={dispatch}
                            camp={camp}
                            isLoadingOrderPreview={isLoadingOrderPreview}
                        />
                    ))}
                </div>
            }

            <div className="pt-1 flex items-start flex-col gap-3 mb-2">
                {isGroup && !isPayingSeparately ? (
                    <PortalHoverTip content="To add line items for a group reservation, you can edit the individual reservations.">
                        <button className="text-gray-300 flex items-center cursor-default" disabled={true}>
                            <PlusIcon className="w-5 h-5 mr-1.5" />
                            Add line item
                        </button>
                    </PortalHoverTip>
                ) : (
                    <button
                        className="cursor-pointer text-green-600 disabled-add-line-item flex items-center"
                        disabled={isLoadingOrderPreview}
                        onClick={() => {
                            const emptyLineItem = {
                                id: "change-me",
                                kind: LINE_ITEM_KINDS.BASIC,
                                label: "",
                                total_amount_in_cents: 0,
                                unit_amount_in_cents: 0,
                                occurrences: null,
                                occurrence_unit: null,
                                quantity: 0,
                                tax_rate: 0,
                                order_id: null,
                                add_on_id: "change-me",
                                reservation_id: null,
                                parent_line_item_id: null,
                                discount_id: null,
                            }
                            setLineItems([...lineItems, emptyLineItem])
                        }}
                    >
                        <PlusIcon className="w-5 h-5 mr-1.5" />
                        Add line item
                    </button>
                )}
            </div>
        </div>
    )
}

export default ResponsivePricingSection